import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Download,
  Copy,
  RefreshCw,
  ExternalLink,
  Users,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useRestaurant } from "@/query/useRestaurant";
import useAuthStore from "@/store/authStore";

export default function QRCodeManagementPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { data: restaurant } = useRestaurant(user?.uid || "");
  const qrCodeRef = useRef<HTMLDivElement>(null);

  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);

  // Generate menu URL
  const menuUrl = restaurant?.id
    ? `${window.location.origin}/menu/${restaurant.id}`
    : "";

  const handleGenerateQRCode = () => {
    if (!restaurant?.id) {
      toast.error(t("menuItem.noRestaurant"));
      return;
    }

    setIsGenerating(true);
    // Simulate generation delay
    setTimeout(() => {
      setQrCodeUrl(menuUrl);
      setIsGenerating(false);
      toast.success(t("qrCode.management.generate"));
    }, 500);
  };

  const handleDownloadQRCode = () => {
    if (!qrCodeRef.current) return;

    const canvas = qrCodeRef.current.querySelector("canvas");
    if (!canvas) return;

    // Create a temporary link element
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${restaurant?.restaurantName || "menu"}-qr-code.png`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(t("qrCode.download"));
  };

  const handleCopyUrl = async () => {
    if (!menuUrl) return;

    try {
      await navigator.clipboard.writeText(menuUrl);
      toast.success(t("qrCode.management.urlCopied"));
    } catch {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = menuUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t("qrCode.management.urlCopied"));
    }
  };

  const handlePreviewMenu = () => {
    if (menuUrl) {
      window.open(menuUrl, "_blank");
    }
  };

  const howToUseSteps = t("qrCode.management.howToUseSteps", {
    returnObjects: true,
  }) as string[];
  const customerFeatures = t("qrCode.management.customerExperienceFeatures", {
    returnObjects: true,
  }) as string[];

  if (!restaurant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          role="status"
          className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"
        ></div>
      </div>
    );
  }

  return (
    <div className={`p-6 max-w-7xl mx-auto space-y-8`}>
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold">{t("qrCode.management.title")}</h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t("qrCode.management.description")}
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        {/* QR Code Generation Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              {t("qrCode.management.generateTitle")}
            </CardTitle>
            <CardDescription>
              {t("qrCode.management.generateDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center space-y-4">
              {qrCodeUrl ? (
                <div
                  ref={qrCodeRef}
                  className="bg-white p-4 rounded-lg shadow-sm"
                >
                  <QRCodeCanvas
                    value={qrCodeUrl}
                    size={200}
                    level="M"
                    includeMargin
                  />
                </div>
              ) : (
                <div className="w-[232px] h-[232px] bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t("qrCode.management.generate")}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleGenerateQRCode}
                  disabled={isGenerating}
                  className="flex items-center gap-2"
                  data-testid="qr-generate-btn"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isGenerating ? "animate-spin" : ""}`}
                  />
                  {qrCodeUrl
                    ? t("qrCode.management.regenerate")
                    : t("qrCode.management.generate")}
                </Button>

                {qrCodeUrl && (
                  <Button
                    onClick={handleDownloadQRCode}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {t("qrCode.management.downloadQR")}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Menu URL Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              {t("qrCode.management.menuUrlTitle")}
            </CardTitle>
            <CardDescription>
              {t("qrCode.management.menuUrlDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={menuUrl}
                readOnly
                className="flex-1"
                placeholder={t("qrCode.management.menuUrlTitle")}
              />
              <Button
                onClick={handleCopyUrl}
                variant="outline"
                size="icon"
                disabled={!menuUrl}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>

            <Button
              onClick={handlePreviewMenu}
              disabled={!menuUrl}
              variant="outline"
              className="w-full flex items-center gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              {t("qrCode.management.previewMenu")}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* How to Use Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            {t("qrCode.management.howToUseTitle")}
          </CardTitle>
          <CardDescription>
            {t("qrCode.management.howToUseDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {howToUseSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </div>
                <p className="text-sm">{step}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Customer Experience Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("qrCode.management.customerExperienceTitle")}
          </CardTitle>
          <CardDescription>
            {t("qrCode.management.customerExperienceDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {customerFeatures.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{feature}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
